import { createContext, useContext, useEffect, useState } from "react";

const ThemeContext = createContext(null);
const KEY = "labtrack_theme";

const resolve = (pref) =>
  pref === "system"
    ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
    : pref;

export const ThemeProvider = ({ children }) => {
  // Default is light; a user's explicit choice is remembered across sign-outs.
  const [pref, setPref] = useState(() => localStorage.getItem(KEY) || "light");
  const [forceLight, setForceLight] = useState(false);

  useEffect(() => {
    const apply = () => {
      const mode = forceLight ? "light" : resolve(pref);
      document.documentElement.classList.toggle("dark", mode === "dark");
      document.documentElement.style.colorScheme = mode;
    };
    apply();
    localStorage.setItem(KEY, pref);
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    if (pref === "system" && !forceLight) {
      mq.addEventListener("change", apply);
      return () => mq.removeEventListener("change", apply);
    }
  }, [pref, forceLight]);

  const mode = forceLight ? "light" : resolve(pref);
  const toggle = () => setPref(mode === "dark" ? "light" : "dark");

  return (
    <ThemeContext.Provider value={{ pref, setPref, mode, toggle, setForceLight }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

/** Pins the page to the light palette while the calling component is mounted. */
export const useForceLight = () => {
  const { setForceLight } = useContext(ThemeContext);
  useEffect(() => {
    setForceLight(true);
    return () => setForceLight(false);
  }, [setForceLight]);
};
